import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadTextureAsset, saveProject } from '../projects/persistence';
import { createDxf, createPortableProjectFile, downloadText, parseProjectFile, safeFilename } from '../projects/transfer';
import { useEditorStore } from '../store/editorStore';

export function ProjectTransfer({ onImported }: { onImported: () => void }) {
  const { t } = useTranslation();
  const project = useEditorStore((state) => state.project);
  const setProject = useEditorStore((state) => state.setProject);
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const base = safeFilename(project.name);

  const importFile = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      if (file.size > 100 * 1024 * 1024) throw new Error(t('transfer.tooLarge'));
      const imported = parseProjectFile(await file.text());
      await saveProject(imported);
      setProject(imported);
      onImported();
    } catch (cause) {
      setError(cause instanceof Error && cause.message === t('transfer.tooLarge') ? cause.message : t('transfer.invalid'));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const downloadProject = async () => {
    setBusy(true);
    setError('');
    try {
      downloadText(`${base}.arborcad.json`, await createPortableProjectFile(project, loadTextureAsset), 'application/json;charset=utf-8');
    } catch {
      setError(t('transfer.textureMissing'));
    } finally {
      setBusy(false);
    }
  };

  return <div className="project-transfer">
    <input ref={input} type="file" accept=".json,.arborcad,application/json" hidden onChange={(event) => void importFile(event.target.files?.[0])} />
    <div className="project-transfer-actions">
      <button disabled={busy} onClick={() => input.current?.click()}>{t('transfer.import')}</button>
      <button disabled={busy} onClick={() => void downloadProject()}>{t('transfer.backup')}</button>
      <button onClick={() => downloadText(`${base}.dxf`, createDxf(project), 'application/dxf')}>{t('transfer.dxf')}</button>
    </div>
    <small>{t('transfer.dxfNote')}</small>
    {error && <p role="alert">{error}</p>}
  </div>;
}
